/*
 * Cart drawer behaviour (sections/nl-cart-drawer.liquid). Loaded with defer from the section, outside the re-rendered
 * panel, after assets/nl-app.js. It builds on the foundation contract (THEME_ARCHITECTURE.md 8): <cart-drawer>
 * (dialog, focus trap via showModal, Esc, overlay click, focus return), NL.cart and the cart events.
 *
 *   line updates      <cart-drawer>.updateLine delegates to lineUpdateHook (set below): busy line + aria-busy panel,
 *                     spinner on the pressed control, controls and checkout disabled during the request, inline
 *                     errors from the cart API (422 stock limits, network) after a refresh, focus restored to the
 *                     same control (or the next line) after the Section Rendering API swaps the markup
 *   announcements     polite live region outside the re-rendered panel ("Nákupní košík: 3 produkty, celkem 947 Kč")
 *   scroll lock       html.nl-cdr-gutter pads the root by the lost scrollbar width while html overflow is hidden
 *   scroll shadows    .is-scrolled (header divider) and .has-more (footer shadow) on the panel
 *   <nl-w2-progress>  free shipping / gift progress, compositor only scaleX from the last shown value
 *   <nl-w2-note>      order note toggle, saved to /cart/update.js without a re-render
 *   motion            new lines slide in, changed quantities flash, removed lines fade, count bump, total tick,
 *                     upsell tiles stagger when the recommendation source changes
 *
 * Motion is transform / opacity only and is skipped under prefers-reduced-motion or when the motion setting is off.
 */
(() => {
  'use strict';

  if (window.NLW2Cart) return;
  window.NLW2Cart = { version: '2.0.0' };

  const NL = (window.NL = window.NL || {});
  const doc = document;
  const reduced = () =>
    NL.motion ? NL.motion.reduced : window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const drawerEl = () => doc.querySelector('cart-drawer');
  const panelOf = (drawer) => drawer && drawer.querySelector('[data-cart-section]');
  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };
  const restartClass = (el, className) => {
    if (!el) return;
    el.classList.remove(className);
    void el.offsetWidth;
    el.classList.add(className);
  };
  const lineByKey = (root, key) =>
    root && key ? root.querySelector(`.nl-cart-line[data-line-key="${CSS.escape(String(key))}"]`) : null;
  const strings = () => NL.strings || {};

  const ALERT_ICON =
    '<svg class="nl-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>';

  /* ==========================================================================
     Scroll lock without layout shift
     ========================================================================== */

  // Classic scrollbars disappear when html gets overflow hidden (nl-components.css): the root gets the lost width
  // back as padding so the header and the page behind the overlay do not jump sideways
  let baseWidth = 0;
  const measureScrollbar = () => {
    if (doc.querySelector('.nl-dialog[open]')) return;
    baseWidth = doc.documentElement.clientWidth;
  };
  const lockGutter = () => {
    const root = doc.documentElement;
    const lost = baseWidth ? root.clientWidth - baseWidth : 0;
    if (lost > 0 && lost < 40) {
      root.style.setProperty('--nl-cdr-sb', `${lost}px`);
      root.classList.add('nl-cdr-gutter');
    }
  };
  const unlockGutter = () => {
    doc.documentElement.classList.remove('nl-cdr-gutter');
    doc.documentElement.style.removeProperty('--nl-cdr-sb');
  };

  /* ==========================================================================
     Live announcements
     ========================================================================== */

  let announceTimer = 0;
  const announce = (drawer, prefix) => {
    if (!drawer) return;
    const live = drawer.querySelector('[data-cart-drawer-live]');
    if (!live) return;
    window.clearTimeout(announceTimer);
    live.textContent = '';
    // Read the message after the re-render settles, one announcement per burst of events
    announceTimer = window.setTimeout(() => {
      const source = drawer.querySelector('[data-cart-drawer-message]');
      if (!source) return;
      live.textContent = [prefix, source.textContent.replace(/\s+/g, ' ').trim()].filter(Boolean).join('. ');
    }, 160);
  };

  /* ==========================================================================
     Scroll shadows
     ========================================================================== */

  const syncScroll = (drawer) => {
    const panel = panelOf(drawer);
    const body = panel && panel.querySelector('[data-cart-drawer-body]');
    if (!panel || !body) return;
    const max = body.scrollHeight - body.clientHeight;
    panel.classList.toggle('is-scrolled', body.scrollTop > 2);
    panel.classList.toggle('has-more', max > 2 && body.scrollTop < max - 2);
  };

  /* ==========================================================================
     Inline errors
     ========================================================================== */

  const errorState = { key: null, message: '' };

  const clearError = (drawer) => {
    errorState.key = null;
    errorState.message = '';
    if (drawer) drawer.querySelectorAll('.nl-cdr__error').forEach((el) => el.remove());
  };

  const showError = (drawer) => {
    if (!drawer || !errorState.message) return;
    drawer.querySelectorAll('.nl-cdr__error').forEach((el) => el.remove());
    const box = doc.createElement('p');
    box.className = 'nl-cdr__error';
    box.setAttribute('role', 'alert');
    box.innerHTML = ALERT_ICON;
    const text = doc.createElement('span');
    text.textContent = errorState.message;
    box.append(text);
    const line = lineByKey(drawer, errorState.key);
    const slot = (line && line.querySelector('[data-line-error-slot]')) || drawer.querySelector('[data-cart-drawer-alert]');
    if (!slot) return;
    slot.append(box);
    if (box.scrollIntoView) box.scrollIntoView({ block: 'nearest', behavior: reduced() ? 'auto' : 'smooth' });
  };

  /* ==========================================================================
     Busy state and focus restore for line updates
     ========================================================================== */

  let busyCount = 0;
  const pressed = { key: null, kind: null };

  const kindOf = (control) => {
    if (!control) return 'input';
    if (control.matches('[data-cart-remove]')) return 'remove';
    if (control.matches('[data-qty-step="1"]')) return 'plus';
    if (control.matches('[data-qty-step="-1"]')) return 'minus';
    return 'input';
  };

  const controlOf = (line, kind) => {
    if (!line) return null;
    if (kind === 'remove') return line.querySelector('[data-cart-remove]');
    if (kind === 'plus') return line.querySelector('[data-qty-step="1"]');
    if (kind === 'minus') return line.querySelector('[data-qty-step="-1"]');
    return line.querySelector('[data-line-quantity]');
  };

  const setCheckoutBusy = (drawer, busy) => {
    const checkout = drawer && drawer.querySelector('[data-cart-checkout]');
    if (!checkout) return;
    if (busy) checkout.setAttribute('aria-disabled', 'true');
    else checkout.removeAttribute('aria-disabled');
  };

  const setLineBusy = (line, kind, busy) => {
    if (!line) return;
    line.classList.toggle('is-busy', busy);
    if (busy) line.setAttribute('aria-busy', 'true');
    else line.removeAttribute('aria-busy');
    line.querySelectorAll('button').forEach((button) => {
      if (busy) {
        button.dataset.w2WasDisabled = button.disabled ? '1' : '';
        button.disabled = true;
      } else if (button.dataset.w2WasDisabled !== undefined) {
        button.disabled = button.dataset.w2WasDisabled === '1';
        delete button.dataset.w2WasDisabled;
      }
    });
    const input = line.querySelector('[data-line-quantity]');
    if (input) input.readOnly = busy;
    const control = controlOf(line, kind);
    if (control && control.tagName === 'BUTTON') control.classList.toggle('is-pressed', busy);
  };

  const focusInDrawer = (drawer, element) => {
    if (!element || !drawer || !drawer.isOpen) return;
    element.focus({ preventScroll: true });
    if (element.scrollIntoView) element.scrollIntoView({ block: 'nearest' });
  };

  const restoreFocus = (drawer, key, kind, index, hadFocus) => {
    if (!drawer || !drawer.isOpen) return;
    // Restore when the visitor was on a control, or when focus fell out of the dialog (a stepper button that turns
    // disabled at 0 drops focus to the body before the request even starts)
    const active = doc.activeElement;
    const lost = !active || active === doc.body || !drawer.contains(active);
    if (!hadFocus && !lost) return;
    const line = lineByKey(drawer, key);
    if (line) {
      let target = controlOf(line, kind);
      if (!target || target.disabled) target = controlOf(line, 'input');
      focusInDrawer(drawer, target);
      return;
    }
    // The line is gone: next line (same position), else the previous one, else the drawer title
    const lines = Array.from(drawer.querySelectorAll('.nl-cart-line'));
    const next = lines[Math.min(index, lines.length - 1)];
    const title = next && next.querySelector('.nl-cart-line__title');
    focusInDrawer(drawer, title || drawer.querySelector('[data-cart-drawer-focus]'));
  };

  async function lineUpdateHook(key, quantity, source) {
    const drawer = this;
    const panel = panelOf(drawer);
    const line = lineByKey(drawer, key);
    const kind = pressed.key === key && pressed.kind ? pressed.kind : kindOf(source);
    const lines = Array.from(drawer.querySelectorAll('.nl-cart-line'));
    const index = Math.max(0, lines.indexOf(line));
    const hadFocus = drawer.contains(doc.activeElement) && doc.activeElement !== drawer.querySelector('[data-cart-drawer-focus]');
    pressed.key = null;
    pressed.kind = null;

    clearError(drawer);
    busyCount += 1;
    if (panel) panel.setAttribute('aria-busy', 'true');
    setCheckoutBusy(drawer, true);
    setLineBusy(line, kind, true);

    try {
      await NL.cart.change(key, quantity, { source: 'cart-drawer' });
      restoreFocus(drawer, key, kind, index, hadFocus);
    } catch (error) {
      errorState.key = key;
      errorState.message = (error && error.message) || strings().errorGeneric || '';
      try {
        await NL.cart.refresh('cart-drawer-error');
      } catch (refreshError) {
        /* offline: keep the current markup, the busy state is cleared below */
      }
      showError(drawer);
      restoreFocus(drawer, key, kind, index, hadFocus);
    } finally {
      busyCount = Math.max(0, busyCount - 1);
      if (busyCount === 0) {
        const current = panelOf(drawer);
        if (current) current.removeAttribute('aria-busy');
        setCheckoutBusy(drawer, false);
      }
      if (line && line.isConnected) setLineBusy(line, kind, false);
    }
  }

  const installHook = () => {
    const CartDrawer = customElements.get('cart-drawer');
    if (CartDrawer && !CartDrawer.prototype.lineUpdateHook) CartDrawer.prototype.lineUpdateHook = lineUpdateHook;
  };
  if (customElements.get('cart-drawer')) installHook();
  else customElements.whenDefined('cart-drawer').then(installHook);

  // Remember which control started the update (the change event arrives later, debounced, from the input)
  doc.addEventListener(
    'click',
    (event) => {
      const control = event.target.closest && event.target.closest('cart-drawer .nl-cart-line :is([data-qty-step], [data-cart-remove])');
      if (!control) return;
      const line = control.closest('.nl-cart-line');
      pressed.key = line ? line.dataset.lineKey : null;
      pressed.kind = kindOf(control);
    },
    true
  );

  // No checkout while the cart is still saving
  doc.addEventListener(
    'submit',
    (event) => {
      const form = event.target.closest && event.target.closest('cart-drawer [data-cart-drawer-form]');
      if (!form) return;
      const checkout = form.querySelector('[data-cart-checkout]');
      // pendingAdds: an add is still on its way (optimistic line), the cart page would open without it
      if (busyCount > 0 || pendingAdds.length > 0 || (checkout && checkout.getAttribute('aria-disabled') === 'true')) {
        event.preventDefault();
        return;
      }
      if (checkout) checkout.setAttribute('aria-busy', 'true');
    },
    true
  );

  // Errors from other cart actions while the drawer is open (upsell add, bundle): shown inline, toasts sit behind the
  // modal overlay
  doc.addEventListener('cart:error', (event) => {
    const drawer = drawerEl();
    const detail = event.detail || {};
    if (!drawer || !drawer.isOpen || detail.source === 'cart-drawer') return;
    errorState.key = null;
    errorState.message = detail.message || strings().cartError || '';
    showError(drawer);
  });

  /* ==========================================================================
     <nl-w2-progress>
     ========================================================================== */

  const shownProgress = new Map();
  const FILL_MS = 480;

  class W2Progress extends HTMLElement {
    connectedCallback() {
      this.fill = this.querySelector('[data-w2-fill]');
      if (!this.fill) return;
      this.key = this.dataset.key || 'default';
      this.target = Math.max(0, Math.min(1, (parseFloat(this.dataset.value) || 0) / 100));

      if (reduced()) {
        this.set(this.target, true);
        shownProgress.set(this.key, this.target);
        return;
      }

      const from = shownProgress.has(this.key) ? shownProgress.get(this.key) : 0;
      const dialog = this.closest('dialog');
      if (!dialog || dialog.open) {
        this.play(from);
      } else {
        // Drawer closed: hold the last shown value, animate when the drawer opens
        this.set(from, true);
        this.from = from;
        this.pending = true;
      }
    }

    set(value, instant) {
      this.fill.classList.toggle('is-instant', Boolean(instant));
      this.fill.style.setProperty('--w2-p', String(value));
    }

    play(fromValue) {
      if (!this.fill) return;
      const from = typeof fromValue === 'number' ? fromValue : this.from || 0;
      const to = this.target;
      this.pending = false;
      shownProgress.set(this.key, to);

      if (reduced() || Math.abs(to - from) < 0.002) {
        this.set(to, true);
        return;
      }

      const crossing = Array.from(this.querySelectorAll('[data-w2-mark]')).filter((mark) => {
        const pos = parseFloat(mark.dataset.w2Mark);
        return to > from && pos > from && pos <= to + 0.0001;
      });
      crossing.forEach((mark) => mark.classList.remove('is-reached', 'is-pop'));

      // Commit the start value with a forced style flush, then set the target in the same task
      this.set(from, true);
      void this.fill.getBoundingClientRect();
      this.set(to, false);
      if (to > from) restartClass(this, 'is-gaining');
      crossing.forEach((mark) => {
        const pos = parseFloat(mark.dataset.w2Mark);
        const share = Math.min(1, (pos - from) / (to - from));
        window.setTimeout(() => {
          mark.classList.add('is-reached');
          restartClass(mark, 'is-pop');
        }, FILL_MS * share * 0.62);
      });
    }
  }

  define('nl-w2-progress', W2Progress);

  /* ==========================================================================
     <nl-w2-note>
     ========================================================================== */

  const note = { open: null, value: null, saved: null, timer: 0 };

  doc.addEventListener('nl:cart:moved', () => {
    window.clearTimeout(note.timer);
    note.saved = '';
    note.value = '';
    note.open = false;
  });

  class W2Note extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.toggleBtn = this.querySelector('[data-w2-note-toggle]');
      this.panel = this.querySelector('[data-w2-note-panel]');
      this.input = this.querySelector('[data-w2-note-input]');
      this.status = this.querySelector('[data-w2-note-status]');
      if (!this.toggleBtn || !this.panel || !this.input) return;
      this._bound = true;

      if (note.saved === null) note.saved = this.input.value;
      // Keep the visitor's open state and latest text across drawer re-renders
      if (note.value !== null && note.value !== this.input.value) this.input.value = note.value;
      if (note.open !== null) this.setOpen(note.open, false);

      this.toggleBtn.addEventListener('click', () => this.setOpen(this.panel.hidden, true));
      this.input.addEventListener('input', () => {
        note.value = this.input.value;
        this.queue();
      });
      this.input.addEventListener('change', () => this.flush());
    }

    setOpen(open, interactive) {
      note.open = open;
      this.panel.hidden = !open;
      this.classList.toggle('is-open', open);
      this.toggleBtn.setAttribute('aria-expanded', String(open));
      if (open && interactive) {
        if (!reduced()) {
          this.panel.classList.add('is-entering');
          void this.panel.offsetWidth;
          this.panel.classList.remove('is-entering');
        }
        this.input.focus({ preventScroll: true });
        if (this.panel.scrollIntoView) this.panel.scrollIntoView({ block: 'nearest', behavior: reduced() ? 'auto' : 'smooth' });
      }
      const drawer = drawerEl();
      if (drawer) window.requestAnimationFrame(() => syncScroll(drawer));
    }

    queue() {
      window.clearTimeout(note.timer);
      this.showStatus('');
      note.timer = window.setTimeout(() => this.flush(), 700);
    }

    async flush() {
      window.clearTimeout(note.timer);
      const value = this.input.value;
      if (value === note.saved) return;
      const previous = note.saved;
      note.saved = value;
      try {
        const base = (NL.routes && NL.routes.cartUpdate) || '/cart/update';
        const write = () => fetch(`${base}.js`, {
          method: 'POST',
          credentials: 'same-origin',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: value }),
        });
        const relay = window.NLLanguageCartRelay;
        const response = await (relay && typeof relay.runMutation === 'function' ? relay.runMutation(write) : write());
        if (!response.ok) throw new Error(String(response.status));
        const current = doc.querySelector('nl-w2-note');
        if (current && typeof current.showStatus === 'function') current.showStatus(current.dataset.saved || '');
      } catch (error) {
        note.saved = previous;
      }
    }

    showStatus(text) {
      if (!this.status) return;
      window.clearTimeout(this.statusTimer);
      this.status.textContent = text;
      this.status.classList.toggle('is-visible', Boolean(text));
      if (text) {
        this.statusTimer = window.setTimeout(() => this.status.classList.remove('is-visible'), 2600);
      }
    }
  }

  define('nl-w2-note', W2Note);

  /* ==========================================================================
     Re-render diff: lines, count, total, upsell
     ========================================================================== */

  const snap = { keys: null, qty: new Map(), count: null, total: null, upsell: null };
  const linesOf = (root) => Array.from(root.querySelectorAll('[data-cart-drawer-lines] > [data-line-key]'));
  const qtyOf = (line) => parseInt(line.dataset.quantity, 10) || 0;
  const textOf = (root, selector) => {
    const el = root.querySelector(selector);
    return el ? el.textContent.replace(/\s+/g, ' ').trim() : null;
  };
  const countOf = (root) => {
    const panel = panelOf(root);
    return panel ? parseInt(panel.dataset.itemCount, 10) || 0 : 0;
  };

  const takeSnapshot = () => {
    const drawer = drawerEl();
    if (!drawer) return;
    const lines = linesOf(drawer);
    snap.keys = new Set(lines.map((line) => line.dataset.lineKey));
    snap.qty = new Map(lines.map((line) => [line.dataset.lineKey, qtyOf(line)]));
    snap.total = textOf(drawer, '[data-cart-drawer-total]');
    snap.count = countOf(drawer);
  };

  const markUpsell = (drawer) => {
    const box = drawer.querySelector('[data-cart-upsell]');
    if (!box) return;
    const id = box.dataset.productId || '';
    if (id && id !== snap.upsell) {
      snap.upsell = id;
      if (!reduced()) box.classList.add('is-w2-enter');
    }
  };

  let addedPrefix = '';

  const onCartUpdated = (event) => {
    const drawer = drawerEl();
    if (!drawer) return;
    const detail = event.detail || {};
    const cart = detail.cart;

    if (!reduced() && snap.keys) {
      linesOf(drawer).forEach((line, index) => {
        const key = line.dataset.lineKey;
        if (!snap.keys.has(key)) {
          line.style.setProperty('--w2-i', String(index));
          line.classList.add('nl-w2-line-in');
        } else if (qtyOf(line) !== snap.qty.get(key)) {
          line.classList.add('nl-w2-line-flash');
        }
      });

      const count = cart && typeof cart.item_count === 'number' ? cart.item_count : null;
      if (count !== null && snap.count !== null && count > snap.count) {
        restartClass(drawer.querySelector('.nl-cdr__count'), 'nl-w2-bump');
      }

      const total = textOf(drawer, '[data-cart-drawer-total]');
      if (snap.total !== null && total !== null && total !== snap.total) {
        restartClass(drawer.querySelector('[data-cart-drawer-total]'), 'nl-w2-tick');
      }
    }

    takeSnapshot();
    if (drawer.isOpen) {
      markUpsell(drawer);
      if (detail.source !== 'pageshow') {
        announce(drawer, addedPrefix);
        addedPrefix = '';
      }
      window.requestAnimationFrame(() => syncScroll(drawer));
    }
  };

  // Removed lines fade out while the request runs (the re-render then drops them; a failed request re-renders them)
  const fadeOut = (line) => {
    if (!line || reduced()) return;
    line.classList.add('nl-w2-line-out');
  };

  doc.addEventListener(
    'click',
    (event) => {
      const remove = event.target.closest && event.target.closest('cart-drawer [data-cart-remove]');
      if (remove) fadeOut(remove.closest('.nl-cart-line'));
    },
    true
  );

  doc.addEventListener('change', (event) => {
    const input = event.target.closest && event.target.closest('cart-drawer [data-line-quantity]');
    if (input && (parseInt(input.value, 10) || 0) === 0) fadeOut(input.closest('.nl-cart-line'));
  });

  // Upsell source: the product added last; announce the add; keep focus inside the open drawer
  doc.addEventListener('cart:item-added', (event) => {
    const drawer = drawerEl();
    if (!drawer) return;
    const items = (event.detail && event.detail.items) || [];
    const first = items[0];
    clearError(drawer);
    addedPrefix = strings().cartAdded || '';

    if (drawer.isOpen) {
      // Added from inside the drawer (upsell): the pressed button was re-rendered away, move focus to the new line
      const active = doc.activeElement;
      if (!active || active === doc.body || !drawer.contains(active)) {
        const line = first && lineByKey(drawer, first.key);
        focusInDrawer(drawer, (line && line.querySelector('.nl-cart-line__title')) || drawer.querySelector('[data-cart-drawer-focus]'));
      }
      announce(drawer, addedPrefix);
      addedPrefix = '';
    }

    if (!first || !first.product_id) return;
    const box = drawer.querySelector('[data-cart-upsell]');
    if (!box) return;
    const id = String(first.product_id);
    if (box.dataset.productId === id) return;
    box.dataset.productId = id;
    if (drawer.isOpen && !box.dataset.loading && typeof drawer.loadUpsell === 'function') {
      drawer.loadUpsell();
      markUpsell(drawer);
    }
  });

  doc.addEventListener('dialog:open', (event) => {
    const drawer = event.target;
    if (!drawer || !drawer.matches || !drawer.matches('cart-drawer')) return;
    lockGutter();
    drawer.querySelectorAll('nl-w2-progress').forEach((progress) => {
      if (progress.pending && typeof progress.play === 'function') progress.play();
    });
    markUpsell(drawer);
    if (addedPrefix) {
      announce(drawer, addedPrefix);
      addedPrefix = '';
    }
    window.requestAnimationFrame(() => syncScroll(drawer));
  });

  // One shot classes must not replay when the closed dialog is shown again (display none restarts CSS animations)
  const TRANSIENT = ['nl-w2-line-in', 'nl-w2-line-flash', 'nl-w2-bump', 'nl-w2-tick', 'is-w2-enter', 'is-pop', 'is-gaining'];
  doc.addEventListener('dialog:close', (event) => {
    const drawer = event.target;
    if (!drawer || !drawer.matches || !drawer.matches('cart-drawer')) return;
    unlockGutter();
    measureScrollbar();
    clearError(drawer);
    TRANSIENT.forEach((name) => drawer.querySelectorAll(`.${name}`).forEach((el) => el.classList.remove(name)));
  });

  // Scroll shadows follow the body scroll (scroll does not bubble: capture on the drawer host)
  const bindScroll = () => {
    const drawer = drawerEl();
    if (!drawer || drawer._w2Scroll) return;
    drawer._w2Scroll = true;
    drawer.addEventListener(
      'scroll',
      (event) => {
        if (event.target.matches && event.target.matches('[data-cart-drawer-body]')) syncScroll(drawer);
      },
      { capture: true, passive: true }
    );
  };

  /* ==========================================================================
     Instant add, every add to cart (owner 16. 10. 2026: „okamžitě“, „přidání do košíku co nejkratší chvíli“).
     NL.cart announces cart:adding right before the request and opens the drawer in the same frame: the optimistic
     line (or the bumped quantity of a line already in the cart) plus the new count, subtotal, estimate and free
     shipping progress show at once. Product facts come from the pairing tile, data-opt-* on the product form, the
     form's variants JSON or the product card around the pressed button (callers may pass them as info). The Section
     Rendering API response replaces the optimistic markup without replaying the slide in; pending adds are
     re-applied after every re-render (two quick taps queue, NL.cart is sequential). A failed add drops its entry,
     refreshes the drawer from the server (the real state) and shows the inline error.
     ========================================================================== */

  const pendingAdds = [];
  const NBSP = ' ';
  const locale = (NL.settings && NL.settings.locale) || document.documentElement.lang || 'cs';
  const formatMoney = (cents) => typeof NL.formatMoney === 'function'
    ? NL.formatMoney(cents, { trimZeros: true }) : String(Number(cents || 0) / 100);
  const parseMoney = (text) => {
    const raw = String(text || '');
    if (/[,.]\d/.test(raw)) return null;
    const digits = raw.replace(/[^\d]/g, '');
    return digits ? parseInt(digits, 10) * 100 : null;
  };
  const pluralRules = new Intl.PluralRules(locale);
  const countLabel = (n) => {
    const forms = (NL.strings && NL.strings.productCount) || {};
    return (forms[pluralRules.select(n)] || forms.other || '%count%').replace(/%count%/g, String(n));
  };
  const CHECK_ICON =
    '<svg class="nl-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
  const escapeHTML = (value) =>
    String(value || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const ADDING =
    '<span class="nl-cart-line__adding"><span class="nl-cart-line__adding-dot" aria-hidden="true"></span>' +
    escapeHTML((NL.strings && NL.strings.cartAdding) || '') + '</span>';

  const buildTileLine = (tile, add) => {
    const media = tile.querySelector('.nl-cart-tile__media');
    const img = media && media.querySelector('img');
    const pack = media && media.classList.contains('nl-cart-tile__media--pack');
    const title = tile.querySelector('.nl-cart-tile__title');
    const claim = tile.querySelector('.nl-cart-tile__claim');
    return (
      `<li class="nl-cart-line nl-cart-line--drawer nl-cart-line--optimistic" data-optimistic="${escapeHTML(add.key)}" aria-busy="true" style="${escapeHTML(tile.getAttribute('style'))}">` +
      `<span class="nl-cart-line__media${pack ? ' nl-cart-line__media--pack' : ''}" aria-hidden="true">${img ? img.outerHTML : ''}</span>` +
      `<div class="nl-cart-line__head"><span class="nl-cart-line__title">${title ? title.innerHTML : ''}</span>` +
      `${claim ? `<p class="nl-claim-line nl-cart-line__claim">${claim.innerHTML}</p>` : ''}</div>` +
      `<div class="nl-cart-line__actions">${ADDING}` +
      `<div class="nl-cart-line__prices"><span class="nl-cart-line__total">${formatMoney(add.price)}</span></div></div></li>`
    );
  };

  const buildLine = (add, info) =>
    `<li class="nl-cart-line nl-cart-line--drawer nl-cart-line--optimistic" data-optimistic="${escapeHTML(add.key)}" aria-busy="true">` +
    `<span class="nl-cart-line__media" aria-hidden="true">${
      info.image ? `<img src="${escapeHTML(info.image)}" alt="" decoding="async">` : '<span class="nl-cart-line__placeholder"></span>'
    }</span>` +
    `<div class="nl-cart-line__head"><span class="nl-cart-line__title">${escapeHTML(info.title)}</span></div>` +
    `<div class="nl-cart-line__actions">${ADDING}${
      Number.isFinite(add.price) ? `<div class="nl-cart-line__prices"><span class="nl-cart-line__total">${formatMoney(add.price)}</span></div>` : ''
    }</div></li>`;

  const textOfEl = (el) => (el ? el.textContent.replace(/\s+/g, ' ').trim() : '');
  const CARD_SCOPE = '.nl-card, .nl-bf-tile, [data-rit-card], [data-opt-scope], article, li';

  // Product facts for an optimistic line: caller info, data-opt-* and the variants JSON of the product form, then
  // the product card around the pressed button
  const resolveInfo = (opener, variantId, given, single) => {
    const info = { title: '', image: '', price: NaN };
    let scope = null;
    if (given) {
      info.title = given.title || '';
      info.image = given.image || '';
      if (Number.isFinite(Number(given.price))) info.price = Number(given.price);
      scope = given.el || null;
    }
    const form = single && opener && opener.closest ? opener.closest('product-form') : null;
    if (form) {
      if (!info.title && form.dataset.optTitle) info.title = form.dataset.optTitle;
      if (!info.image && form.dataset.optImage) info.image = form.dataset.optImage;
      const script = form.querySelector('script[data-variants]');
      if (script && (!Number.isFinite(info.price) || !info.title)) {
        try {
          const variant = (JSON.parse(script.textContent) || []).find((v) => String(v.id) === variantId);
          if (variant) {
            if (!Number.isFinite(info.price) && Number.isFinite(variant.price)) info.price = variant.price;
            if (!info.title && variant.name) info.title = String(variant.name).replace(/ - Default Title$/, '');
            if (!info.image && variant.featured_image && variant.featured_image.src) info.image = variant.featured_image.src;
          }
        } catch (e) {
          /* no variants JSON: card facts below */
        }
      }
      if (!Number.isFinite(info.price) && form.dataset.optPrice) info.price = parseInt(form.dataset.optPrice, 10);
    }
    // One button adding several products (pair, bundle): the card around it describes none of them in particular
    if (!scope && single && opener && opener.closest) scope = opener.closest(CARD_SCOPE);
    if (scope) {
      if (!info.title) info.title = textOfEl(scope.querySelector('[class*="__title"], [class*="-title"], h2, h3, h4'));
      if (!info.image) {
        const img = scope.querySelector('img');
        if (img) info.image = img.currentSrc || img.getAttribute('src') || '';
      }
      if (!Number.isFinite(info.price) && scope.dataset && scope.dataset.price) info.price = parseInt(scope.dataset.price, 10);
    }
    return info;
  };

  const markTileAdded = (tile) => {
    if (!tile || tile.classList.contains('is-added')) return;
    tile.classList.add('is-added');
    const button = tile.querySelector('.nl-cart-tile__add');
    if (!button) return;
    button.dataset.w2Html = button.innerHTML;
    button.dataset.w2Label = button.getAttribute('aria-label') || '';
    button.classList.add('is-added');
    button.innerHTML = CHECK_ICON;
    const title = textOfEl(tile.querySelector('.nl-cart-tile__title'));
    const addedLabel = (NL.strings && (NL.strings.cartAddedNamed || NL.strings.added)) || button.dataset.w2Label;
    button.setAttribute('aria-label', addedLabel.replace(/%title%/g, title));
  };

  const unmarkTile = (tile) => {
    tile.classList.remove('is-added');
    const button = tile.querySelector('.nl-cart-tile__add');
    if (!button || button.dataset.w2Html === undefined) return;
    button.classList.remove('is-added');
    button.innerHTML = button.dataset.w2Html;
    button.setAttribute('aria-label', button.dataset.w2Label);
    delete button.dataset.w2Html;
    delete button.dataset.w2Label;
  };

  const isPending = (variantId) => pendingAdds.some((add) => add.variantId === variantId);
  const existingLine = (root, variantId) =>
    root ? root.querySelector(`[data-cart-drawer-lines] > [data-line-key^="${CSS.escape(variantId)}:"]`) : null;

  // Empty drawer: the server render has no line list, count badge or footer yet. The optimistic frame gets the same
  // structure (list, badge, subtotal and a waiting checkout), so the server render then only fills it in.
  const emptyFrames = new WeakMap();
  const ensureList = (panel) => {
    let list = panel.querySelector('[data-cart-drawer-lines]');
    if (list) return list;
    const body = panel.querySelector('[data-cart-drawer-body]');
    if (!body) return null;
    // Kept to put the empty state back when the add fails and no server render replaces the frame (offline, 429)
    emptyFrames.set(panel, { html: panel.innerHTML, cls: panel.className });
    body.classList.remove('nl-cdr__empty');
    body.innerHTML = '<div class="nl-cdr__alert" data-cart-drawer-alert></div><ul class="nl-cdr__lines" role="list" data-cart-drawer-lines></ul>';
    panel.classList.remove('nl-cdr--empty');
    panel.classList.add('is-optimistic');
    const title = panel.querySelector('#CartDrawerTitle');
    if (title && !title.querySelector('.nl-cdr__count')) {
      title.insertAdjacentHTML(
        'beforeend',
        '<span class="nl-cdr__count" data-cart-drawer-count="0"><span aria-hidden="true">0</span><span class="visually-hidden"></span></span>'
      );
    }
    if (!panel.querySelector('.nl-cdr__footer')) {
      const t = strings();
      body.insertAdjacentHTML(
        'afterend',
        '<footer class="nl-cdr__footer">' +
          `<div class="nl-cdr__summary"><p class="nl-cdr__summary-label">${escapeHTML(t.cartSubtotal || 'Mezisoučet')} ` +
          '<span class="nl-cdr__summary-count" data-cart-summary-count></span></p>' +
          `<p class="nl-cdr__summary-total" data-cart-drawer-total>${formatMoney(0)}</p></div>` +
          `<div class="nl-cdr__form"><span class="nl-btn nl-btn--block nl-cdr__checkout" aria-busy="true" aria-hidden="true"><span>${escapeHTML(t.cartCheckout || 'Objednat')}</span></span></div>` +
          '</footer>'
      );
    }
    list = body.querySelector('[data-cart-drawer-lines]');
    return list;
  };

  // Brings the current render in line with pendingAdds: adds what is on its way, takes back what failed
  const applyOptimistic = (drawer) => {
    const panel = panelOf(drawer);
    if (!panel) return;
    const totalEl = panel.querySelector('[data-cart-drawer-total]');
    const touched = Boolean(totalEl && totalEl.dataset.base !== undefined);
    if (!pendingAdds.length && !touched && !panel.querySelector('[data-optimistic], [data-optimistic-qty]')) return;

    if (!pendingAdds.length && panel.classList.contains('is-optimistic') && emptyFrames.has(panel)) {
      const frame = emptyFrames.get(panel);
      emptyFrames.delete(panel);
      panel.className = frame.cls;
      panel.innerHTML = frame.html;
      const base = parseInt(panel.dataset.itemCount, 10) || 0;
      snap.count = base;
      doc.querySelectorAll('cart-count').forEach((el) => {
        if (typeof el.render === 'function') el.render(base);
      });
      return;
    }

    const keys = new Set(pendingAdds.map((add) => add.key));
    panel.querySelectorAll('[data-optimistic]').forEach((line) => {
      if (!keys.has(line.dataset.optimistic)) line.remove();
    });
    panel.querySelectorAll('.nl-cart-pairing .nl-cart-tile.is-added').forEach((tile) => {
      if (!isPending(tile.dataset.variantId)) unmarkTile(tile);
    });

    // Lines already in the cart: show the summed quantity (server value plus what is on its way)
    const merged = new Map();
    pendingAdds.forEach((add) => {
      if (add.merge) merged.set(add.variantId, (merged.get(add.variantId) || 0) + add.quantity);
    });
    panel.querySelectorAll('[data-optimistic-qty]').forEach((line) => {
      const variantId = String(line.dataset.lineKey || '').split(':')[0];
      if (merged.has(variantId)) return;
      line.removeAttribute('data-optimistic-qty');
      line.removeAttribute('aria-busy');
      const input = line.querySelector('[data-line-quantity]');
      if (input) input.value = line.dataset.quantity || input.value;
    });
    merged.forEach((extraQty, variantId) => {
      const line = existingLine(panel, variantId);
      if (!line) return;
      const input = line.querySelector('[data-line-quantity]');
      const base = parseInt(line.dataset.quantity, 10) || 0;
      if (!line.hasAttribute('data-optimistic-qty') && !reduced()) restartClass(line, 'nl-w2-line-flash');
      line.setAttribute('data-optimistic-qty', '');
      line.setAttribute('aria-busy', 'true');
      if (input) input.value = String(base + extraQty);
    });

    let extra = 0;
    let unknownPrice = false;
    let list = null;
    pendingAdds.forEach((add) => {
      if (Number.isFinite(add.price)) extra += add.price;
      else unknownPrice = true;
      if (add.tile) {
        panel
          .querySelectorAll(`.nl-cart-pairing .nl-cart-tile[data-variant-id="${CSS.escape(add.variantId)}"]`)
          .forEach(markTileAdded);
      }
      if (add.merge) return;
      if (!list) list = ensureList(panel);
      if (list && !list.querySelector(`[data-optimistic="${CSS.escape(add.key)}"]`)) {
        // Shopify lists a newly added line first: the optimistic line sits where the real one will appear
        list.insertAdjacentHTML('afterbegin', add.html);
        const line = list.firstElementChild;
        if (line && !reduced() && !add.shown) {
          line.style.setProperty('--w2-i', '0');
          line.classList.add('nl-w2-line-in');
        }
        add.shown = true;
      }
    });

    // Totals: the base is the server value of the current render (stored on the element the re-render replaces).
    // Without a known price for every pending line the server total stays until the response. Queried again: an
    // empty drawer gets its subtotal only from ensureList above.
    const totalEl2 = panel.querySelector('[data-cart-drawer-total]');
    if (totalEl2 && !unknownPrice) {
      const totalEl = totalEl2;
      if (totalEl.dataset.base === undefined) totalEl.dataset.base = String(parseMoney(totalEl.textContent) ?? '');
      const base = parseInt(totalEl.dataset.base, 10);
      if (Number.isFinite(base)) {
        const total = base + extra;
        totalEl.textContent = formatMoney(total);
        snap.total = textOf(drawer, '[data-cart-drawer-total]');

        // One short line under the subtotal (owner 16. 10. 2026 19:30): "doprava zdarma" once the threshold is reached
        const estimate = panel.querySelector('[data-cart-estimate]');
        if (estimate) {
          const threshold = parseInt(estimate.dataset.threshold, 10);
          const free = Number.isFinite(threshold) && threshold > 0 && total >= threshold;
          const text = free ? estimate.dataset.textFree : estimate.dataset.text;
          if (text) estimate.textContent = text;
        }

        const progress = panel.querySelector('nl-w2-progress[data-ship]');
        const ship = progress ? parseInt(progress.dataset.ship, 10) : NaN;
        if (progress && progress.fill && Number.isFinite(ship) && ship > 0) {
          const from = parseFloat(progress.fill.style.getPropertyValue('--w2-p')) || 0;
          progress.target = Math.min(1, total / ship);
          if (typeof progress.play === 'function') progress.play(from);
          const track = progress.querySelector('[role="progressbar"]');
          if (track) track.setAttribute('aria-valuenow', String(Math.round(progress.target * 100)));
          const message = progress.querySelector('.nl-w2-progress__message');
          if (message) {
            if (message.dataset.w2Orig === undefined) {
              message.dataset.w2Orig = message.innerHTML;
              progress.dataset.w2Complete = progress.classList.contains('is-complete') ? '1' : '';
            }
            if (total >= ship) {
              message.textContent = progress.dataset.reachedText || '';
              progress.classList.add('is-complete');
            } else {
              message.innerHTML = message.dataset.w2Orig;
              progress.classList.toggle('is-complete', progress.dataset.w2Complete === '1');
              const amount = message.querySelector('strong');
              if (amount) amount.textContent = formatMoney(ship - total);
            }
          }
        }
      }
    }

    // Checkout waits (spinner) until the add is saved, so the cart page never opens without the new product
    const checkout = panel.querySelector('[data-cart-checkout]');
    if (checkout) {
      if (pendingAdds.length) {
        checkout.setAttribute('aria-busy', 'true');
        checkout.dataset.w2Adding = '1';
      } else if (checkout.dataset.w2Adding) {
        checkout.removeAttribute('aria-busy');
        delete checkout.dataset.w2Adding;
      }
    }

    // Count: badge, the summary label and the header badge
    const baseCount = parseInt(panel.dataset.itemCount, 10) || 0;
    const count = baseCount + pendingAdds.reduce((sum, add) => sum + add.quantity, 0);
    const badge = panel.querySelector('.nl-cdr__count');
    if (badge) {
      badge.dataset.cartDrawerCount = String(count);
      const shown = badge.querySelector('[aria-hidden="true"]');
      if (shown) shown.textContent = String(count);
      const hidden = badge.querySelector('.visually-hidden');
      if (hidden) hidden.textContent = countLabel(count);
      if (!reduced() && snap.count !== null && count > snap.count) restartClass(badge, 'nl-w2-bump');
    }
    snap.count = count;
    const summaryCount = panel.querySelector('[data-cart-summary-count]');
    if (summaryCount) summaryCount.textContent = countLabel(count);
    doc.querySelectorAll('cart-count').forEach((el) => {
      if (typeof el.render === 'function') el.render(count);
    });
  };

  // A pairing tile already on its way: no second line from a double tap
  doc.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form || !form.closest || !form.closest('cart-drawer .nl-cart-pairing product-form')) return;
      const tile = form.closest('.nl-cart-tile');
      const variantId = tile && tile.dataset.variantId;
      if (variantId && isPending(variantId)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  doc.addEventListener('cart:adding', (event) => {
    const detail = event.detail || {};
    const drawer = drawerEl();
    if (!drawer || !detail.open || !Array.isArray(detail.lines) || !detail.lines.length) return;
    const panel = panelOf(drawer);
    const opener = detail.opener;
    const tile = opener && opener.closest ? opener.closest('cart-drawer .nl-cart-tile') : null;
    clearError(drawer);
    detail.lines.forEach((line, index) => {
      const variantId = String(line.id);
      const quantity = Math.max(1, Number(line.quantity) || 1);
      const add = { token: detail.token, key: `${detail.token}-${index}`, variantId, quantity, price: NaN, html: '', shown: false, merge: false, tile: false };
      const props = line.properties && Object.keys(line.properties).length > 0;
      if (tile && detail.lines.length === 1 && tile.dataset.variantId === variantId) {
        const unit = parseInt(tile.dataset.price, 10);
        add.tile = true;
        add.price = Number.isFinite(unit) ? unit * quantity : NaN;
      } else {
        const info = resolveInfo(opener, variantId, Array.isArray(detail.info) ? detail.info[index] : null, detail.lines.length === 1);
        add.price = Number.isFinite(info.price) ? info.price * quantity : NaN;
        add.info = info;
      }
      if (!props && existingLine(panel, variantId)) add.merge = true;
      else add.html = add.tile ? buildTileLine(tile, add) : buildLine(add, add.info);
      pendingAdds.push(add);
    });
    applyOptimistic(drawer);
  });

  // Registered after onCartUpdated (below): the snapshot and its slide in classes exist when this runs
  const onPendingUpdated = (event) => {
    const drawer = drawerEl();
    const token = event.detail && event.detail.token;
    if (token) {
      for (let i = pendingAdds.length - 1; i >= 0; i -= 1) {
        const add = pendingAdds[i];
        if (add.token !== token) continue;
        pendingAdds.splice(i, 1);
        // The real line replaces the optimistic one in place: no second slide in
        if (drawer && add.shown) {
          drawer
            .querySelectorAll(`[data-cart-drawer-lines] > [data-line-key^="${CSS.escape(add.variantId)}:"]`)
            .forEach((line) => line.classList.remove('nl-w2-line-in'));
        }
      }
    }
    if (drawer) applyOptimistic(drawer);
  };

  doc.addEventListener('cart:error', (event) => {
    const detail = event.detail || {};
    if (!pendingAdds.length) return;
    const before = pendingAdds.length;
    if (detail.token) {
      for (let i = pendingAdds.length - 1; i >= 0; i -= 1) if (pendingAdds[i].token === detail.token) pendingAdds.splice(i, 1);
    } else if (detail.source === 'product-form') {
      // NL.cart is sequential: the failed add is the oldest one still pending
      pendingAdds.shift();
    }
    if (pendingAdds.length === before) return;
    // Take it back at once, then sync with the server (when the refresh fails too, the reverted markup stays) and
    // show why
    const drawer = drawerEl();
    if (drawer) applyOptimistic(drawer);
    const message = detail.message || strings().cartError || '';
    const restore = () => {
      if (drawer) applyOptimistic(drawer);
      errorState.key = null;
      errorState.message = message;
      showError(drawer);
    };
    if (NL.cart && typeof NL.cart.refresh === 'function') NL.cart.refresh('cart-drawer-error').then(restore, restore);
    else restore();
  });

  /* ==========================================================================
     Vyprázdnit košík (owner 16. 10. 2026: „tlačítko vyprázdnit košík, které smaže vše z košíku“), drawer header and
     cart page. [data-cart-clear] opens the inline confirm it controls (aria-controls), Ano runs NL.cart.clear
     (POST /cart/clear.js with the cart sections, one round trip), Zpět or Escape closes it. The cart as it was stays
     in memory for 6 s: "Vrátit zpět" adds the same variants, quantities, properties (_nl_ritual) and selling plans
     back through NL.cart.add (/cart/add.js), in the drawer with the instant lines. The countdown pauses while the
     mouse rests on the bar. The cart is read once more when the confirm opens, so Ano does not wait for it.
     ========================================================================== */

  const UNDO_MS = 6000;
  const clearState = { gen: 0, snapshot: null, snapshotGen: -1 };
  let undo = null;

  const confirmOf = (trigger) => (trigger ? doc.getElementById(trigger.getAttribute('aria-controls') || '') : null);
  const triggerOf = (confirm) =>
    confirm && confirm.id ? doc.querySelector(`[data-cart-clear][aria-controls="${CSS.escape(confirm.id)}"]`) : null;

  const prefetchCart = () => {
    if (!NL.cart || typeof NL.cart.get !== 'function') return;
    const gen = clearState.gen;
    NL.cart.get().then(
      (cart) => {
        if (gen !== clearState.gen || pendingAdds.length) return;
        clearState.snapshot = cart;
        clearState.snapshotGen = gen;
      },
      () => {}
    );
  };
  // Only a copy read after the last cart change counts (any update bumps the generation)
  const freshSnapshot = () =>
    clearState.snapshot && clearState.snapshotGen === clearState.gen && !pendingAdds.length && busyCount === 0
      ? clearState.snapshot
      : null;

  const setConfirm = (trigger, open, moveFocus) => {
    const confirm = confirmOf(trigger);
    if (!confirm) return;
    confirm.hidden = !open;
    trigger.setAttribute('aria-expanded', String(open));
    if (open) {
      prefetchCart();
      if (!reduced()) restartClass(confirm, 'is-entering');
      const yes = confirm.querySelector('[data-cart-clear-yes]');
      if (moveFocus && yes) yes.focus({ preventScroll: true });
    } else if (moveFocus && trigger.isConnected) {
      trigger.focus({ preventScroll: true });
    }
    const drawer = trigger.closest('cart-drawer');
    if (drawer) window.requestAnimationFrame(() => syncScroll(drawer));
  };

  const sizedImage = (src) => (src ? `${src}${String(src).includes('?') ? '&' : '?'}width=192` : '');

  // Shopify lists the newest line first: the lines go back oldest first, so the order stays as it was
  const entriesOf = (cart) =>
    (cart && Array.isArray(cart.items) ? cart.items : [])
      .slice()
      .reverse()
      .map((item) => {
        const line = { id: item.variant_id, quantity: item.quantity };
        if (item.properties && typeof item.properties === 'object' && Object.keys(item.properties).length) {
          line.properties = item.properties;
        }
        const plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
        if (plan && plan.id) line.selling_plan = plan.id;
        const price = Number.isFinite(Number(item.final_price)) ? Number(item.final_price) : Number(item.price);
        return { line, key: item.key, info: { title: item.product_title || item.title || '', image: sizedImage(item.image), price } };
      });

  const focusAfterUndo = (state) => {
    if (state.inDrawer) {
      const drawer = drawerEl();
      if (drawer && drawer.isOpen) focusInDrawer(drawer, drawer.querySelector('[data-cart-drawer-focus]'));
      return;
    }
    const target = state.host && state.host.querySelector('a[href], button:not([disabled])');
    if (target) target.focus({ preventScroll: true });
  };

  const dropUndo = (expired) => {
    if (!undo) return;
    const state = undo;
    undo = null;
    window.clearTimeout(state.timer);
    if (!state.bar.isConnected) return;
    const hadFocus = state.bar.contains(doc.activeElement);
    state.bar.remove();
    if (expired && hadFocus) focusAfterUndo(state);
  };

  const showUndo = (slot, host, snapshot, text, inDrawer) => {
    dropUndo(false);
    const bar = doc.createElement('div');
    bar.className = 'nl-cart-undo';
    bar.setAttribute('data-cart-undo', '');
    bar.style.setProperty('--undo-ms', `${UNDO_MS}ms`);
    const message = doc.createElement('p');
    message.className = 'nl-cart-undo__text';
    message.textContent = text.cleared;
    const button = doc.createElement('button');
    button.type = 'button';
    button.className = 'nl-cart-undo__btn';
    button.setAttribute('data-cart-undo-btn', '');
    button.textContent = text.undo;
    if (text.undoLabel) button.setAttribute('aria-label', text.undoLabel);
    const timer = doc.createElement('span');
    timer.className = 'nl-cart-undo__timer';
    timer.setAttribute('aria-hidden', 'true');
    bar.append(message, button, timer);
    if (slot === host) host.prepend(bar);
    else slot.append(bar);

    const state = { bar, host, snapshot, inDrawer, remaining: UNDO_MS, started: 0, timer: 0 };
    const run = () => {
      state.started = performance.now();
      state.timer = window.setTimeout(() => {
        if (undo === state) dropUndo(true);
      }, Math.max(0, state.remaining));
    };
    bar.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse' || undo !== state) return;
      window.clearTimeout(state.timer);
      state.remaining -= performance.now() - state.started;
      bar.classList.add('is-paused');
    });
    bar.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse' || undo !== state || !bar.classList.contains('is-paused')) return;
      bar.classList.remove('is-paused');
      run();
    });
    undo = state;
    run();
    return button;
  };

  const runClear = async (yes) => {
    const confirm = yes.closest('[data-cart-clear-confirm]');
    if (!confirm || yes.disabled || !NL.cart || typeof NL.cart.clear !== 'function') return;
    const drawer = yes.closest('cart-drawer');
    const inDrawer = Boolean(drawer);
    const host = inDrawer ? panelOf(drawer) : yes.closest('[data-cart-section]');
    const text = {
      cleared: confirm.dataset.cleared || '',
      undo: confirm.dataset.undo || '',
      undoLabel: confirm.dataset.undoLabel || '',
      error: confirm.dataset.error || strings().cartError || '',
    };

    dropUndo(false);
    if (inDrawer) clearError(drawer);
    confirm.querySelectorAll('button').forEach((button) => {
      button.disabled = true;
    });
    yes.setAttribute('aria-busy', 'true');
    if (host) {
      host.setAttribute('aria-busy', 'true');
      host.classList.add('is-clearing');
      if (inDrawer) host.querySelectorAll('[data-cart-drawer-lines] > .nl-cart-line').forEach(fadeOut);
    }
    if (inDrawer) setCheckoutBusy(drawer, true);

    try {
      const result = await NL.cart.clear({
        source: inDrawer ? 'cart-clear' : 'cart-page-clear',
        before: freshSnapshot() || undefined,
      });
      clearState.snapshot = null;
      if (host) {
        host.removeAttribute('aria-busy');
        host.classList.remove('is-clearing');
      }
      if (inDrawer) setCheckoutBusy(drawer, false);
      const before = result && result.before;
      if (!host || !before || !Array.isArray(before.items) || !before.items.length) return;
      // The sections are re-rendered by now: the undo goes into the empty drawer (alert slot) or on top of the page
      const slot = (inDrawer && host.querySelector('[data-cart-drawer-alert]')) || host;
      const button = showUndo(slot, host, before, text, inDrawer);
      if (!inDrawer || drawer.isOpen) button.focus({ preventScroll: true });
    } catch (error) {
      // Nothing changed on the server (or we cannot tell): show the real state and say so
      try {
        await NL.cart.refresh(inDrawer ? 'cart-drawer-error' : 'cart-page-error');
      } catch (refreshError) {
        /* offline: the current markup stays */
      }
      if (host) {
        host.removeAttribute('aria-busy');
        host.classList.remove('is-clearing');
        host.querySelectorAll('.nl-w2-line-out').forEach((line) => line.classList.remove('nl-w2-line-out'));
      }
      confirm.querySelectorAll('button').forEach((button) => {
        button.disabled = false;
      });
      yes.removeAttribute('aria-busy');
      if (inDrawer) {
        setCheckoutBusy(drawer, false);
        errorState.key = null;
        errorState.message = text.error;
        showError(drawer);
      } else if (typeof NL.toast === 'function') {
        NL.toast(text.error, { variant: 'error' });
      }
    }
  };

  const restoreCart = async () => {
    if (!undo || !NL.cart || typeof NL.cart.add !== 'function') return;
    const state = undo;
    undo = null;
    window.clearTimeout(state.timer);
    const button = state.bar.querySelector('[data-cart-undo-btn]');
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    state.bar.classList.add('is-busy');
    const entries = entriesOf(state.snapshot);
    if (!entries.length) {
      state.bar.remove();
      return;
    }
    const lines = entries.map((entry) => entry.line);
    // Drawer: the instant lines show at once (facts from the saved cart); cart page: the page re-renders on its own
    const options = state.inDrawer
      ? { source: 'cart-undo', optimistic: entries.map((entry) => entry.info) }
      : { source: 'cart-undo', open: false };
    try {
      await NL.cart.add(lines, options);
    } catch (error) {
      // A product may have sold out meanwhile: put back one by one what is still missing; every refusal shows its
      // own message (inline in the drawer, a toast on the cart page)
      let now = null;
      try {
        now = await NL.cart.get();
      } catch (getError) {
        now = null;
      }
      const have = new Set(((now && now.items) || []).map((item) => item.key));
      for (const entry of entries) {
        if (have.has(entry.key)) continue;
        try {
          await NL.cart.add([entry.line], { source: 'cart-undo', open: false });
        } catch (lineError) {
          if (!state.inDrawer && typeof NL.toast === 'function') NL.toast(lineError.message, { variant: 'error' });
        }
      }
    }
    if (state.bar.isConnected) state.bar.remove();
    if (!state.inDrawer) {
      const active = doc.activeElement;
      if (!active || active === doc.body) {
        const first = state.host && state.host.querySelector('li[data-line-key] a[href]:not([tabindex="-1"])');
        if (first) first.focus({ preventScroll: true });
      }
    }
  };

  doc.addEventListener('nl:cart:updated', () => {
    clearState.gen += 1;
  });

  doc.addEventListener('click', (event) => {
    const target = event.target && event.target.closest ? event.target : null;
    if (!target) return;
    const trigger = target.closest('[data-cart-clear]');
    if (trigger) {
      event.preventDefault();
      setConfirm(trigger, trigger.getAttribute('aria-expanded') !== 'true', true);
      return;
    }
    const no = target.closest('[data-cart-clear-no]');
    if (no) {
      event.preventDefault();
      const back = triggerOf(no.closest('[data-cart-clear-confirm]'));
      if (back) setConfirm(back, false, true);
      return;
    }
    const yes = target.closest('[data-cart-clear-yes]');
    if (yes) {
      event.preventDefault();
      runClear(yes);
      return;
    }
    if (target.closest('[data-cart-undo-btn]')) {
      event.preventDefault();
      restoreCart();
    }
  });

  // Escape inside the confirm closes the confirm only (the drawer stays open)
  doc.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape' || !event.target || !event.target.closest) return;
      const confirm = event.target.closest('[data-cart-clear-confirm]');
      const trigger = confirm ? triggerOf(confirm) : event.target.closest('[data-cart-clear][aria-expanded="true"]');
      if (!trigger || trigger.getAttribute('aria-expanded') !== 'true') return;
      event.preventDefault();
      event.stopPropagation();
      setConfirm(trigger, false, true);
    },
    true
  );

  doc.addEventListener('dialog:close', (event) => {
    const drawer = event.target;
    if (!drawer || !drawer.matches || !drawer.matches('cart-drawer')) return;
    drawer.querySelectorAll('[data-cart-clear][aria-expanded="true"]').forEach((trigger) => setConfirm(trigger, false, false));
  });

  // Theme event (nl:cart:updated) only: apps fire a bare "cart:updated" on every cart fetch
  doc.addEventListener('nl:cart:updated', onCartUpdated);
  doc.addEventListener('nl:cart:updated', onPendingUpdated);
  doc.addEventListener('shopify:section:load', () => {
    takeSnapshot();
    bindScroll();
  });
  window.addEventListener('resize', () => {
    measureScrollbar();
    const drawer = drawerEl();
    if (drawer && drawer.isOpen) syncScroll(drawer);
  });

  // The scrollbar width is measured when the main thread is idle (and again on load), not at boot: reading
  // clientWidth here forced a layout in the middle of the deferred scripts
  const init = () => {
    takeSnapshot();
    bindScroll();
    if ('requestIdleCallback' in window) window.requestIdleCallback(measureScrollbar, { timeout: 1500 });
    else window.setTimeout(measureScrollbar, 600);
  };
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  window.addEventListener('load', measureScrollbar, { once: true });
})();
